import { Link, useParams } from 'react-router-dom';
import { ReactionsBar } from 'src/components/reactionsBar';
import { PostType, Reaction } from 'src/hook/usePosts';
import { PostInformationBar } from 'src/pages/CommunityDetails/Posts/components/postInformationBar';
import { MediaDisplayFormat, PostMediaThumbnail } from 'src/components/PostMediaThumbnail';
import { IMAGES } from 'src/assets';
import { DateUtils } from 'src/utils/date';
import { useTrackEvents } from 'src/context/userProfileContext/hooks';
import { useUserProfile } from 'src/context/userProfileContext';
import { TryResult } from 'src/utils/datahubDefaultEvent';
import { ACCESS_TO_POST } from 'src/utils/constants';
import { AuthorName, PostTitle, ThumbnailWrapper } from './post.styles';
import { DispatchPostReactionEventParams, PathParams, PostProps } from './post.types';

const Post = ({ post }: PostProps) => {
  const { lang } = useParams() as PathParams;
  const { userUcode, loggedUserUcode } = useUserProfile();
  const { sendPostDetailEvent, sendReactPostEvent } = useTrackEvents({
    loggedUserUcode
  });

  const showThumbnail = post.type === PostType.IMAGE || post.type === PostType.VIDEO;
  const date = DateUtils.compareDates(post.pubDate);
  const isCollaborative = post?.type === PostType.COLLABORATIVE;
  const communityPath = `/${lang}/club/${post.membershipSlug}/community/${post.communityId}`;
  const basePostPath = isCollaborative
    ? `${communityPath}/post/${post.id}/collaborative`
    : `${communityPath}/post/${post.id}`;

  const postPath = `${basePostPath}?origin=${ACCESS_TO_POST.USER_PROFILE}`;

  const medals = {
    1: IMAGES.MEDAL_1ST,
    2: IMAGES.MEDAL_2ND,
    3: IMAGES.MEDAL_3RD
  };

  const medal = post.authorFeaturedMemberRank ? medals[post.authorFeaturedMemberRank] : undefined;
  const communityAndMembership =
    post?.communityName && post.membershipName
      ? `${post.communityName} - ${post.membershipName}`
      : post.communityName;

  const handleDispatchPostDetailEvent = () => {
    sendPostDetailEvent(post);
  };

  const handleReactionSuccess = () => {
    sendReactPostEvent({
      result: TryResult.SUCCESSFUL,
      userUcode
    });
  };

  const handleReactionError = ({ statusCode, errorMessage }: DispatchPostReactionEventParams) => {
    sendReactPostEvent({
      result: TryResult.FAILURE,
      statusCode,
      failureDescription: errorMessage,
      userUcode
    });
  };

  return (
    <div className="_border-bottom _border-gray-200 _px-3 _py-4">
      <Link
        to={postPath}
        title={post.title}
        className="_text-gray-900 _text-decoration-none"
        onClick={handleDispatchPostDetailEvent}
      >
        <header>
          <div className="_d-flex _align-items-center">
            <AuthorName className="_text-gray-900 _font-weight-bold _line-height _mr-3">
              {post.authorName}
            </AuthorName>

            {medal && <img src={medal} className="_mr-1" alt={post.authorName} />}
            <span className="_text-1 _text-gray-500 _text-nowrap">{date}</span>
          </div>

          <span className="_text-1 _text-gray-600">{communityAndMembership}</span>
        </header>

        <div className="_d-flex _align-items-center _pt-2">
          <PostTitle
            className="_text-2 _font-weight-bold _overflow-hidden _mb-0"
            hasThumbnail={showThumbnail}
          >
            {post?.title}
          </PostTitle>

          {showThumbnail && (
            <ThumbnailWrapper className="_ml-2">
              <PostMediaThumbnail
                id={post.id}
                mediaType={post.type as PostType}
                displayFormat={MediaDisplayFormat.MiniSquare}
                className="_max-w-full"
              />
            </ThumbnailWrapper>
          )}
        </div>
      </Link>

      <footer>
        <ReactionsBar
          className="_border-bottom _border-gray-200 _py-2"
          postId={post?.id as number}
          reactionsParam={post?.reactions as Reaction[]}
          onReactionSuccess={handleReactionSuccess}
          onReactionError={handleReactionError}
        />

        <PostInformationBar
          className="_pt-4"
          commentsCount={post.commentsCount}
          trending={post.trending}
          viewsCount={post.viewsCount}
          isCommentsEnabled
        />
      </footer>
    </div>
  );
};

export default Post;
